import React, { memo, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ILogsState } from 'types/logs'
import { getLogs } from 'utils/actions/logs'
import { Pagination, Spinner } from 'transfix-ui'
import { logsColumnOrder } from 'constants/logs'
import { LogsRow } from './LogsRow'
import { IConnectParams } from './types'

const perPage = 20

export const Logs = memo<IConnectParams>(({ objId, objType }) => {
  const filters = useMemo(() => ({ objType, objId, provideDetails: true }), [objType, objId])
  const dispatch = useDispatch()

  useEffect(() => {
    if (objId) {
      dispatch(getLogs({ filters, pageCount: 0, perPage }))
    }
  }, [objId, dispatch, filters])

  const {
    count,
    error,
    fetching,
    firstLoad,
    params: { pageCount },
    versions,
  } = useSelector<{ logs: ILogsState }, ILogsState>(({ logs }) => logs)

  const applyPagination = (newPage: number) => {
    dispatch(getLogs({ filters, perPage, pageCount: newPage - 1 }))
  }

  const ready = !fetching && !error
  let content
  if (firstLoad && fetching) {
    content = (
      <span className='pa5'>
        <Spinner position='relative' />
      </span>
    )
  } else if (ready && !versions.length) {
    content = (
      <span className='pa5'>
        <p className='tc'>
          No {objType.replace(/application/gi, '').toLowerCase()} logs available.
        </p>
      </span>
    )
  } else {
    const refreshPage = () => {
      global.location.reload()
    }
    content = (
      <>
        <table className='table'>
          <thead>
            {logsColumnOrder.map(({ key, label, size }) => (
              <th key={key} className={size}>
                {label}
              </th>
            ))}
          </thead>
          <tbody>
            {error && (
              <tr>
                <td colSpan={logsColumnOrder.length} className='tc'>
                  <span className='pa5'>
                    <span className='pv3'>
                      Sorry, there was an issue loading results. Please try refreshing your page.
                    </span>
                    <button className='btn-text-link' onClick={refreshPage}>
                      Refresh your page
                    </button>
                  </span>
                </td>
              </tr>
            )}
            {fetching ? (
              <tr>
                <td aria-label='Loading logs' colSpan={logsColumnOrder.length} className='tc'>
                  <span className='pa5'>
                    <Spinner position='relative' />
                  </span>
                </td>
              </tr>
            ) : (
              versions.map(version => <LogsRow key={version.uuid} version={version} />)
            )}
          </tbody>
        </table>
        <div className='tc'>
          <Pagination
            currentPage={pageCount + 1}
            numberOfPages={Math.ceil(count / perPage)}
            onPageChange={applyPagination}
          />
        </div>
      </>
    )
  }

  return (
    <div className='row' data-test='logs-tab-content'>
      {content}
    </div>
  )
})
